import api from '@/lib/axios';
import { fetchSSEStream } from '@/lib/fetchStream';
import { ensureAiTaskCredits } from '@/lib/aiTaskCredits';
import { notifyPointBalanceChanged } from '@/lib/creditBalance';
import { normalizeGeneratedFormFieldNames } from '@/lib/domUtils';
import {
  Project,
  UpdateProjectParams,
  CreateProjectParams,
  ProjectDetailParams,
  CreateProjectSessionsParams,
  PublishProjectParams,
  PublishProjectResponse,
  SaveGoogleAdsTagRequest,
  SaveGoogleAdsPostbackRequest,
  ProjectGoogleAdsConfigVO,
  SaveGeneralSettingRequest,
  ProjectGeneralSettingVO,
  SaveSeoConfigRequest,
  ProjectSeoConfigVO,
  WebsiteVersionRecord,
  ListProjectVersionsParams,
  RollbackProjectVersionParams,
  DeleteProjectParams,
  GenerateWebpageStreamParams,
  GenerateWebpageDoneData,
  GenerateWebpageStreamCallbacks,
  CreateWebpagePreviewParams,
  GetPermanentPreviewParams,
  WebpageDraft,
  SaveDraftParams,
  GetLatestDraftParams,
  ListDraftParams,
  UpdateDraftStatusParams,
  WebpageDraftListResponse,
} from '@/types/project';

/**
 * 项目模块 API
 * 封装项目创建、更新、列表查询及详情获取
 */
export const projectApi = {
  /**
   * 更新项目基础信息
   * @param data 更新参数
   */
  updateProject: ({
    userProjectId,
    projectId,
    projectName = null,
    projectLogo = null,
    projectDescription = null,
    publishStatus = null,
  }: UpdateProjectParams): Promise<Project> => {
    return api.post('/lowcode-business/api/user/project/update', {
      userProjectId,
      projectId,
      projectName,
      projectLogo,
      projectDescription,
      publishStatus,
    });
  },

  /**
   * 获取项目列表
   */
  getProjectList: (): Promise<Project[]> => {
    return api.post('/lowcode-business/api/user/project/list', {});
  },

  /**
   * 获取项目详情
   * @param data 详情查询参数
   */
  getProjectDetail: (data: ProjectDetailParams): Promise<Project> => {
    return api.post('/lowcode-business/api/user/project/detail', data);
  },

  /**
   * 创建项目会话
   * @param data 详情查询参数
   */
  createProjectSessions: (data: CreateProjectSessionsParams): Promise<string | number> => {
    return api.post(`/lowcode-business/api/user/agent-session/create`, data);
  },

  /**
   * 创建新项目（projectType=999：AI 网页类型，不调用外部服务）
   * @param data 创建项目参数
   */
  createProject: ({
    projectName = null,
    projectLogo = null,
    projectDescription = null,
    firstUsePrompt = null,
    projectTypeId = null,
  }: CreateProjectParams): Promise<Project> => {
    return api.post('/lowcode-business/api/user/project/create', {
      projectName,
      projectLogo,
      projectDescription,
      firstUsePrompt,
      projectTypeId,
    });
  },

  /**
   * 发布项目
   * @param data 发布参数
   */
  publishProject: (
    data: PublishProjectParams,
    options?: { suppressErrorToast?: boolean },
  ): Promise<PublishProjectResponse> => {
    return api.post('/lowcode-business/api/user/project/publish', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    });
  },

  /**
   * 保存 Google Ads Tag 配置
   * configType=1: 手动代码（tagCode 必填）
   * configType=2: Tag ID（tagId 必填，格式 AW-XXXXXXXXX）
   */
  saveGoogleAdsTag: (data: SaveGoogleAdsTagRequest): Promise<ProjectGoogleAdsConfigVO> => {
    return api.post('/lowcode-business/api/user/project/google-ads/tag/save', data);
  },

  saveGeneralSetting: (
    data: SaveGeneralSettingRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<ProjectGeneralSettingVO> => {
    return api.post('/lowcode-business/api/user/project/general-setting/save', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    });
  },

  /**
   * 保存 SEO / Metadata 配置（首次自动创建，后续更新）
   * POST /lowcode-business/api/user/project/seo/save
   */
  saveSeoConfig: (
    data: SaveSeoConfigRequest,
    options?: { suppressErrorToast?: boolean },
  ): Promise<ProjectSeoConfigVO> => {
    return api.post('/lowcode-business/api/user/project/seo/save', data, {
      _suppressErrorToast: options?.suppressErrorToast,
    });
  },

  /**
   * 查询 Google Ads 完整配置（未配置时返回 null）
   * POST /lowcode-business/api/user/project/google-ads/detail
   */
  getGoogleAdsDetail: (params: {
    userProjectId?: number;
    projectId?: number;
  }): Promise<ProjectGoogleAdsConfigVO | null> => {
    return api.post('/lowcode-business/api/user/project/google-ads/detail', params);
  },

  /**
   * 保存 Google Ads 数据回传开关配置
   * POST /lowcode-business/api/user/project/google-ads/postback/save
   */
  saveGoogleAdsPostback: (
    data: SaveGoogleAdsPostbackRequest,
  ): Promise<ProjectGoogleAdsConfigVO> => {
    return api.post('/lowcode-business/api/user/project/google-ads/postback/save', data);
  },

  getGeneralSettingDetail: (params: {
    userProjectId?: number;
    projectId?: number;
  }): Promise<ProjectGeneralSettingVO | null> => {
    return api.post('/lowcode-business/api/user/project/general-setting/detail', params);
  },

  /**
   * 查询 SEO 配置详情（未配置时返回 null）
   * POST /lowcode-business/api/user/project/seo/detail
   */
  getSeoDetail: (params: {
    userProjectId?: number;
    projectId?: number;
  }): Promise<ProjectSeoConfigVO | null> => {
    return api.post('/lowcode-business/api/user/project/seo/detail', params);
  },

  /**
   * 查询项目网站版本历史列表（按创建时间倒序）
   * POST /lowcode-business/api/user/project/versions
   */
  getProjectVersions: (data: ListProjectVersionsParams): Promise<WebsiteVersionRecord[]> => {
    return api.post('/lowcode-business/api/user/project/versions', data);
  },

  /**
   * 回滚到指定历史版本
   * POST /lowcode-business/api/user/project/versions/rollback
   */
  rollbackProjectVersion: (data: RollbackProjectVersionParams): Promise<PublishProjectResponse> => {
    return api.post('/lowcode-business/api/user/project/versions/rollback', data);
  },

  /**
   * 软删除指定项目（级联删除站点目录并禁用租户）
   * POST /lowcode-business/api/user/project/delete
   */
  deleteProject: (data: DeleteProjectParams): Promise<void> => {
    return api.post('/lowcode-business/api/user/project/delete', data);
  },

  /**
   * AI 网页流式生成（SSE）
   * 事件类型：delta（内容片段）、done（完成，携带 draftId/version）、truncated（被代理截断，草稿未保存）、error（失败）
   * 生成完成后后端自动保存草稿，无需再调 /draft/save
   * @returns AbortController 可用于取消流
   */
  generateWebpageStream: (
    params: GenerateWebpageStreamParams,
    callbacks: GenerateWebpageStreamCallbacks,
  ): AbortController => {
    const controller = new AbortController();
    const generateTaskCode =
      typeof params.flowOptions?.generateTaskCode === 'string'
        ? params.flowOptions.generateTaskCode
        : '';
    const creditsModalTracking = {
      creditsModalProductArea: 'studio',
      creditsModalSurface: 'studio_page_generation',
      creditsModalStage: 'generate_start',
      creditsModalTrigger: 'precheck',
      creditsModalUiType: 'modal' as const,
      creditsModalSource: 'project_generate_webpage_stream',
      creditsModalOperation: 'studio_page_generation',
      creditsModalMetadata: {
        user_project_id: params.userProjectId,
        flow_type: params.flowType || '',
        generate_task_code: generateTaskCode,
      },
    };

    ensureAiTaskCredits(generateTaskCode, {
      showCreditsModal: true,
      creditsModalCopy:
        "You don't have enough credits to generate this page. Top up your account to continue.",
      ...creditsModalTracking,
    })
      .then(() =>
        fetchSSEStream<GenerateWebpageDoneData>(
          '/lowcode-business/api/user/project/webpage/generate/stream',
          params,
          {
            onMessage: (text: string) => callbacks.onDelta(text),
            onHeartbeat: (payload?: string) => callbacks.onHeartbeat?.(payload),
            onRetrying: (payload?: string) => callbacks.onRetrying?.(payload),
            showCreditsModal: Boolean(generateTaskCode),
            creditsModalCopy:
              "You don't have enough credits to generate this page. Top up your account to continue.",
            ...creditsModalTracking,
            creditsModalTrigger: 'backend_stream',
            creditsModalTaskCode: generateTaskCode,
            contentEventNames: ['delta'],
            acceptImplicitContent: false,
          },
          {},
          controller.signal,
        ),
      )
      .then((res) => {
        notifyPointBalanceChanged();
        if (res.truncated) {
          callbacks.onTruncated?.();
          return;
        }
        if (res.finalData) {
          const parsed = res.finalData;
          callbacks.onDone({
            draftId: parsed.draftId ?? '',
            version: String(parsed.version ?? ''),
          });
          return;
        }

        callbacks.onError(
          res.content
            ? 'The generation stream was interrupted before completion. Please retry.'
            : 'The generation stream disconnected before any data was received. Please retry.',
          res.content ? 'INTERRUPTED' : 'FAILED',
        );
      })
      .catch((err: Error) => {
        notifyPointBalanceChanged();
        const code = (err as Error & { code?: string }).code;
        callbacks.onError(err.message, code);
      });

    return controller;
  },

  /**
   * 为指定草稿生成一次性快照预览链接
   * POST /lowcode-business/api/user/project/webpage/preview/create
   * @returns 预览 URL 字符串
   */
  createWebpagePreview: (data: CreateWebpagePreviewParams): Promise<string> => {
    return api.post('/lowcode-business/api/user/project/webpage/preview/create', data);
  },

  /**
   * 查询项目级永久预览 URL（始终渲染最新草稿）
   * POST /lowcode-business/api/user/project/webpage/preview/permanent
   * 首次保存草稿前调用将返回错误
   * @returns 永久预览 URL 字符串
   */
  getPermanentPreviewUrl: (data: GetPermanentPreviewParams): Promise<string> => {
    return api.post('/lowcode-business/api/user/project/webpage/preview/permanent', data, {
      _suppressErrorToast: true,
    });
  },

  /**
   * 查询指定项目当前最新草稿（项目尚无草稿时返回 null）
   * POST /lowcode-business/api/user/project/webpage/draft/latest
   */
  getLatestDraft: (
    userProjectId: number,
    draftStatuses?: GetLatestDraftParams['draftStatuses'],
  ): Promise<WebpageDraft | null> => {
    return api.post('/lowcode-business/api/user/project/webpage/draft/latest', {
      userProjectId,
      draftStatuses,
    });
  },

  /**
   * AI 网页-保存草稿
   * POST /lowcode-business/api/user/project/webpage/draft/save
   */
  saveDraft: (
    data: SaveDraftParams,
    options?: { suppressErrorToast?: boolean },
  ): Promise<WebpageDraft> => {
    const normalizedData =
      data.contentType === 'MARKDOWN'
        ? data
        : {
            ...data,
            content: normalizeGeneratedFormFieldNames(data.content),
          };

    return api.post('/lowcode-business/api/user/project/webpage/draft/save', normalizedData, {
      _suppressErrorToast: options?.suppressErrorToast,
    });
  },

  /**
   * AI 网页-草稿列表
   * POST /lowcode-business/api/user/project/webpage/draft/list
   */
  listDrafts: (data: ListDraftParams): Promise<WebpageDraftListResponse> => {
    return api.post('/lowcode-business/api/user/project/webpage/draft/list', data);
  },

  /**
   * AI 网页-更新草稿状态
   * POST /lowcode-business/api/user/project/webpage/draft/updateStatus
   */
  updateDraftStatus: (data: UpdateDraftStatusParams): Promise<void> => {
    return api.post('/lowcode-business/api/user/project/webpage/draft/updateStatus', data);
  },
};
